import { CSSProperties } from 'react';
import { useLingui } from '@lingui/react/macro';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { useCompanies } from '@/models/company';
import { hasPermission } from '@/models/user';
import { useCurrentOrganization, useCurrentSession } from '@/services/auth';

export const InvoiceDestinationSelect = ({
  invoiceDestination,
  setInvoiceDestination,
  style,
}: {
  invoiceDestination: InvoiceDestination;
  setInvoiceDestination: (destination: InvoiceDestination) => void;
  style?: CSSProperties;
}) => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();
  const [currentOrganization] = useCurrentOrganization();

  const { data, isFetching } = useCompanies({
    limit: 100,
    traits: ['aftersales', 'store'],
  });

  const companyOptions = (data?.companies ?? []).map((company) => ({
    type: 'company' as const,
    label: company.name,
    id: company.id,
  }));

  const clientOption = {
    type: 'client' as const,
    label: t({ id: 'accounting.invoices.destination.client', message: 'Client' }),
    id: undefined,
  };

  const showClientOption = hasPermission(currentSession, 'view_invoice', {
    organizationId: currentOrganization?.id,
  });

  const options = showClientOption ? [clientOption, ...companyOptions] : companyOptions;

  const selectedOption =
    options.find(
      (option) => option.type === invoiceDestination?.type && option.id === invoiceDestination?.id
    ) ?? null;

  if (!options.length) {
    return null;
  }

  return (
    <InputSelect
      variant="select"
      placeholder={t({
        id: 'accounting.invoices.destination.placeholder.all',
        message: 'Recipient: All',
      })}
      aria-label={t({ id: 'accounting.invoices.destination.label', message: 'Recipient' })}
      value={selectedOption}
      isClearable
      options={options}
      getOptionValue={(option) => `${option.type}-${option.id}`}
      getOptionLabel={(option) => option.label}
      onChange={(option, action) => {
        if (action.action === 'clear') {
          setInvoiceDestination({ type: 'all' });
        } else if (option) {
          setInvoiceDestination({ type: option.type, id: option.id });
        }
      }}
      isLoading={isFetching}
      style={style}
    />
  );
};

export type InvoiceDestination = {
  type: 'all' | 'client' | 'company';
  id?: string;
};
